/**
 * Versies die per potje worden vastgelegd.
 *
 * Een lopend potje blijft op de versie waarmee het begon: zo kan een regelcorrectie
 * nooit een potje-in-uitvoering omgooien of een oude event-log onherspeelbaar maken.
 * Nieuwe potjes krijgen de huidige waarden.
 */

/**
 * Gedrag van de regelkernel. Verhogen zodra een regel anders uitpakt.
 *
 * 2 — de audit tegen het regelboekje: een gestolen grondstof kan alleen naar een eigen
 * landschap van dat type ("Grondstoffen kunnen alleen geroofd worden, als je ze op je landen
 * van dat type kunt plaatsen", p. 11 §5), de Alchimist controleert zijn getal, de Spion
 * noemt de kaart bij naam, en er is een `ruil-af` bijgekomen voor het ruilen bij een volle
 * hand (p. 10). Bestaande zetten spelen daardoor anders af.
 *
 * 3 — de speler kiest waar de code lootte. De Rooftocht steelt in beide richtingen via
 * `kies-buit`/`plaats-buit` in plaats van loten (p. 11 §5), de Handelaar neemt "1 of 2"
 * (nieuwe zet `kies-klaar`) en geeft de ene grondstof terug naar keuze via
 * `handelaar-terug` (p. 12 §9), en de Karavaan ruilt via een eigen vraag in plaats van
 * `giveRandom` (p. 12 §10). Daarbovenop drie dichtgetimmerde randgevallen: `kies-buit`
 * eist dat de dief de grondstof kwijt kan, `plaats-buit` weigert een volle tegel, en het
 * handelsvoordeel opent geen keuze meer zonder geldig antwoord. Andere rng-volgorde én
 * andere vragen — oude logs spelen hier anders af.
 *
 * 4 — de landen. De trekstapel is boekvast geworden: elf landen (2× elk producerend
 * landschap, 1 rivierbedding — p. 16) in plaats van een 6×6-reconstructie die nooit
 * opraakte. De grens per speler (`maxLandscapes: 11`) is weg: het boekje kent alleen het
 * opraken van de gedeelde stapel. En bij elk nieuw dorp kiest de speler zelf welk land
 * boven en welk onder komt (nieuwe vraag `leg-landschap`, p. 9).
 *
 * 5 — de echte getallen. Thijs las de fysieke kaarten voor: de startsets van rood en
 * blauw hebben een váste paring van landschap en getal (rood oogst graan op 1, blauw op
 * 2), en de elf landen van de trekstapel dragen hun eigen gedrukte getallen. Tot nu toe
 * werden de startparingen geschud en waren de stapelgetallen een reconstructie. Zelfde
 * seed, ander bord — dus ophogen.
 *
 * 6 — één kaartruil per beurt. "Uitsluitend wanneer een speler in zijn beurt zijn hand
 * niet mag aanvullen, mag hij **één** ontwikkelingskaart ruilen" (p. 10). `ruil-kaart` en
 * `ruil-af` zetten nu een vlag die tot de beurtwissel elke tweede ruil tegenhoudt; de
 * lus hand-vol → ruilen → pakken → wéér ruilen is daarmee dicht. Aanvullen met ruimte in
 * de hand blijft onbeperkt tot de limiet.
 *
 * 7 — de Burgeroorlog raakt beide spelers, ook als één kant niets heeft staan. De
 * overslaan-route wees hetzelfde lege doelwit twee keer aan, waardoor de hele
 * gebeurtenis verdampte zodra de eerste aangewezene geen ridders of vloten had — en de
 * ridder van de ander onterecht bleef staan (vierde echte potje: Willem bleef staan
 * terwijl Chi niets te verliezen had). "Iedere speler moet een ridderkaart of vloot
 * terug in de hand nemen" (p. 13 §13).
 *
 * 8 — de bouwvoorraad is gedeeld: "7 straten. 5 dorpen. 7 steden." (p. 16) is één
 * voorraad voor beide spelers, wie het eerst komt bouwt — hier stond een verzonnen
 * grens per speler (5/7/7), en die weigerde een zesde dorp terwijl de voorraad er nog
 * twee had. En de Verkenner legt zijn twee gekozen landen niet meer zelf neer: net als
 * bij een gewoon dorp kiest de speler daarna via `leg-landschap` welke kant elk land
 * krijgt.
 *
 * 9 — het handelsvoordeel volgt de molen. "Als de molen gedobbeld wordt, mag de speler
 * mét de houten molen de ander een grondstof afnemen. … Als niemand de molen bezit,
 * vervalt deze gebeurtenis" (p. 7b) — en de molen vereist minstens één stad (p. 6).
 * Hier won de kale handelspunten-leider, dus vóór de eerste stad kon je al stelen met
 * alleen een paar vloten. Het riddertoernooi blijft punten-gebaseerd (p. 7a): die
 * asymmetrie is van het boekje.
 */
export const RULES_VERSION = 9;

/**
 * Kaartdataset (kosten, punten, aantallen). Verhogen bij elke datawijziging.
 *
 * 2 — de gebeurtenisstapel klopte niet: pagina 16 telt tien kaarten, wij hadden er zeven.
 * Goed jaar, Vooruitgang en Pest horen er elk twee keer in te zitten. Gevolg van de fout:
 * elke enkelvoudige gebeurtenis werd in 1 op 7 gevallen getrokken in plaats van 1 op 10 —
 * ruim veertig procent te vaak, en juist bij Bouwmeester, Conflict en Burgeroorlog.
 */
export const CARDS_VERSION = 2;

/** Berichtformaat tussen client en server. Mismatch ⇒ "ververs de app". */
export const PROTOCOL_VERSION = 1;

export interface GameVersions {
  readonly rules: number;
  readonly cards: number;
}

export const CURRENT_VERSIONS: GameVersions = {
  rules: RULES_VERSION,
  cards: CARDS_VERSION,
};
